// Phase SIKA-5 Layout — SIKA Goal Page (5-panel layout).
// SIKA-5c/C: inline SIKA Chat restored as Panel 5 per canonical prototype
// (Strikza (1)/screens/sika.jsx) + video walkthrough frame 22.
//
// Source: SIKA_Independence_Architecture_v1.0 §02.1 + §04 + §09 + §11 N12.
// Video walkthrough frames 1/4/7/9/10/11/22.
//
// Layout:
//   - Goal selector tabs (top-right)
//   - Panel 01 Goal Command (HORIZONTAL row)
//   - 2-col grid: Panel 02 Weekly Intel | Panel 04 Protection Vault
//   - Panel 03 Plan Timeline (full-width line chart)
//   - Panel 05 SIKA Chat (inline; full workspace at /sika/chat)
//
// Doctrine:
//   N01 — All 5 panels render regardless of linked_chain_id.
//   N11 — Chat is the 5th panel, inline. /sika/chat = deep-link full workspace.
//   N12 — Bankroll advisory NEVER blocks; surfaces inside Panel 1.
//   Promotion-not-restriction copy throughout.

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuthSession } from "../../auth/useAuthSession.ts";
import { useUserTier } from "../../auth/useUserTier.ts";
import { getBrowserClient } from "../../supabase/browser.ts";
import { useSikaGoal } from "../../sika/useSikaGoal.ts";
import { useSikaGoals } from "../../sika/useSikaGoals.ts";
import {
  computeWeeklyExposure,
  formatExposureAdvisory,
} from "../../sika/checkBankrollExposure.ts";
import { Eyebrow } from "../../components/super-acca";
import { GoalCommandPanel } from "../../components/sika-extras/GoalCommandPanel.tsx";
import { WeeklyIntelPanel } from "../../components/sika-extras/WeeklyIntelPanel.tsx";
import { PlanTimelinePanel } from "../../components/sika-extras/PlanTimelinePanel.tsx";
import { ProtectionVaultPanel } from "../../components/sika-extras/ProtectionVaultPanel.tsx";
import { GoalSelectorTabs } from "../../components/sika-extras/GoalSelectorTabs.tsx";
import { SikaChatPanel } from "../../components/sika-extras/SikaChatPanel.tsx";
import { Pill } from "../../components/super-acca";
import {
  EditGoalModal,
  type EditGoalModalChain,
} from "../../components/sika-extras/EditGoalModal.tsx";

export function SikaGoalPage(): JSX.Element {
  const { user } = useAuthSession();
  const { goals, loading: goalsLoading, refetch: refetchGoals } = useSikaGoals();
  const [searchParams] = useSearchParams();
  const [selectedGoalId, setSelectedGoalId] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [chains, setChains] = useState<EditGoalModalChain[]>([]);
  const [bankroll, setBankroll] = useState<number | null>(null);

  // SIKA-5c/B: any non-terminal status counts as "active" for selection
  // purposes. Goals created via the chat onboarding land in 'in_progress'
  // before the first cycle settles; previously they fell through here.
  const TERMINAL_STATUSES = new Set(["completed", "archived", "cancelled"]);
  const activeGoals = useMemo(
    () => goals.filter((g) => !TERMINAL_STATUSES.has(g.status)),
    [goals],
  );

  // URL-driven selection: ?goal=<id> wins over local state; otherwise
  // single-active falls through; else first active; else first goal of
  // any status (so the Goal Command panel hydrates instead of showing
  // the empty state when the only goal is in a non-active status).
  const urlGoalId = searchParams.get("goal");
  const effectiveGoalId =
    selectedGoalId
    ?? (urlGoalId && activeGoals.some((g) => g.id === urlGoalId) ? urlGoalId : null)
    ?? (activeGoals.length > 0 ? activeGoals[0]!.id : null)
    ?? (goals.length > 0 ? goals[0]!.id : null);

  const sika = useSikaGoal(effectiveGoalId);
  const { isPro } = useUserTier();

  useEffect(() => {
    if (!user) {
      setChains([]);
      setBankroll(null);
      return;
    }
    const supabase = getBrowserClient();
    void (async () => {
      const { data } = await supabase
        .from("rollover_chains")
        .select("id,current_value,status")
        .eq("user_id", user.id)
        .in("status", ["active", "pending_first"])
        .order("created_at", { ascending: false });
      const rows = (data ?? []) as Array<{
        id: string;
        current_value: number;
        status: string;
      }>;
      setChains(rows);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sb = supabase as any;
      const { data: br } = await sb
        .from("user_bankrolls")
        .select("total_bankroll,is_active")
        .eq("user_id", user.id)
        .maybeSingle();
      const total = br && typeof br.total_bankroll === "number" && br.is_active !== false
        ? Number(br.total_bankroll)
        : null;
      setBankroll(total);
    })();
  }, [user]);

  const exposureGoals = useMemo(
    () =>
      activeGoals.map((g) => {
        if (g.id !== effectiveGoalId || sika.cycles.length === 0) {
          return { id: g.id, weekly_stake: null };
        }
        const lastCycle = sika.cycles[sika.cycles.length - 1];
        return {
          id: g.id,
          weekly_stake: lastCycle?.stake_amount ?? null,
        };
      }),
    [activeGoals, effectiveGoalId, sika.cycles],
  );
  const exposure = computeWeeklyExposure(exposureGoals, bankroll);
  const bankrollAdvisory =
    activeGoals.length === 0
      ? null
      : {
          breach: exposure.breach,
          message: formatExposureAdvisory(exposure),
        };

  const goalForEdit = sika.goal
    ? {
        id: sika.goal.id,
        title: sika.goal.title,
        target_amount: sika.goal.target_amount,
        deadline: sika.goal.deadline,
        mode: sika.goal.mode,
        linked_chain_id: sika.goal.linked_chain_id,
      }
    : null;

  // Pick the latest visible cycle for the Slip Detail card + GoalCommand dynamic target
  const latestCycle = useMemo(() => {
    const visible = sika.cycles.filter((c) =>
      ["published", "locked", "settled"].includes(c.status),
    );
    if (visible.length === 0) return null;
    return visible.reduce((latest, cur) =>
      cur.cycle_number > latest.cycle_number ? cur : latest,
    visible[0]!);
  }, [sika.cycles]);
  const legs = latestCycle ? (sika.legsByCycle[latestCycle.cycle_id] ?? []) : [];

  // Empty state — zero goals
  if (!goalsLoading && goals.length === 0) {
    return (
      <div
        className="sa-root"
        style={{
          padding: 24,
          maxWidth: 1180,
          margin: "0 auto",
          color: "var(--sa-fg-primary)",
          background: "var(--sa-bg-base)",
          minHeight: "100%",
        }}
        data-page="sika-goal"
      >
        <HeaderBar tabsSlot={null} />
        <GoalCommandPanel goal={null} pro={isPro} />
      </div>
    );
  }

  // useSikaGoals returns DB-typed rows (linked_chain_ids: string[] | null).
  // The chain chip in the tabs only needs to know IF a chain is linked.
  const tabGoals = activeGoals.map((g) => {
    const firstChainId = Array.isArray(g.linked_chain_ids) && g.linked_chain_ids.length > 0
      ? g.linked_chain_ids[0]
      : null;
    return {
      id: g.id,
      title: g.title,
      mode: firstChainId ? ("chain" as const) : ("simple" as const),
      linked_chain_id: firstChainId ?? null,
    };
  });

  return (
    <div
      className="sa-root"
      style={{
        padding: "20px 24px",
        maxWidth: 1180,
        margin: "0 auto",
        color: "var(--sa-fg-primary)",
        background: "var(--sa-bg-base)",
        minHeight: "100%",
        fontFamily: "var(--sa-font-body)",
      }}
      data-page="sika-goal"
    >
      <style>{`
        @media (max-width: 1023px) {
          .sika-panels-grid { grid-template-columns: minmax(0, 1fr) !important; }
        }
      `}</style>
      <HeaderBar
        tabsSlot={
          tabGoals.length > 0 ? (
            <GoalSelectorTabs
              goals={tabGoals}
              selectedGoalId={effectiveGoalId}
              onSelect={(id) => setSelectedGoalId(id)}
              onEditGoal={sika.goal ? () => setEditOpen(true) : undefined}
            />
          ) : null
        }
      />

      {sika.error ? (
        <div
          style={{
            marginBottom: 14,
            padding: 10,
            border: "1px solid var(--sa-signal-red)",
            borderRadius: 3,
            fontFamily: "var(--sa-font-mono)",
            fontSize: 12,
            color: "var(--sa-signal-red)",
          }}
        >
          <Eyebrow color="var(--sa-signal-red)">SIKA unavailable</Eyebrow>
          <div style={{ marginTop: 6 }}>{sika.error}</div>
        </div>
      ) : null}

      {/* Panel 01 — Goal Command. Horizontal row, full width. */}
      <section style={panelShellStyle}>
        <GoalCommandPanel
          goal={sika.goal}
          pro={isPro}
          latestCycle={latestCycle}
          onEditGoal={sika.goal ? () => setEditOpen(true) : undefined}
        />
      </section>

      {/* Panels 02 + 04 — explicit 2-col grid at desktop widths.
          1.4fr / 1fr ratio (Panel 02 wider because the slip lives inside).
          Falls back to single col below 1024px via @media in inline style trick:
          we set the column template wide; the minmax(0,…) lets contents shrink. */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr)",
          gap: 18,
          marginBottom: 18,
        }}
        data-grid="panels-2-4"
        data-grid-cols="2"
        className="sika-panels-grid"
      >
        <section style={{ ...panelShellStyle, marginBottom: 0 }}>
          <WeeklyIntelPanel
            goal={sika.goal}
            cycles={sika.cycles}
            legs={legs}
            pro={isPro}
          />
        </section>
        <section style={{ ...panelShellStyle, marginBottom: 0 }}>
          <ProtectionVaultPanel
            goal={sika.goal}
            milestones={sika.milestones}
            pro={isPro}
            chainProtection={sika.chainProtection}
          />
        </section>
      </div>

      {/* Panel 03 — Plan Timeline. Full-width. SIKA-5b/3 lands the line chart. */}
      <section style={panelShellStyle}>
        <PlanTimelinePanel goal={sika.goal} cycles={sika.cycles} pro={isPro} />
      </section>

      {/* Panel 05 — SIKA Chat (inline). SIKA-5c/C: canonical prototype keeps
          chat as Panel 5 of 5; the full deep-link workspace remains at
          /sika/chat for long sessions. Both use the same useSikaChat hook. */}
      <section style={panelShellStyle}>
        <SikaChatPanel goalId={effectiveGoalId} pro={isPro} />
      </section>

      {/* Bankroll advisory — small subtle footer pill (Gap #5). Never dominant. */}
      {bankrollAdvisory ? (
        <div
          style={{ marginBottom: 18, display: "flex", justifyContent: "flex-start" }}
          data-testid="sika-bankroll-advisory-footer"
        >
          <Pill
            borderColor={
              bankrollAdvisory.breach
                ? "var(--sa-accent-saffron)"
                : "var(--sa-hairline)"
            }
            color={
              bankrollAdvisory.breach
                ? "var(--sa-accent-saffron)"
                : "var(--sa-fg-muted)"
            }
            background={
              bankrollAdvisory.breach ? "rgba(255,200,64,0.06)" : "transparent"
            }
          >
            <span
              data-breach={bankrollAdvisory.breach}
              style={{ textTransform: "none", letterSpacing: "0.02em" }}
            >
              {bankrollAdvisory.message}
            </span>
          </Pill>
        </div>
      ) : null}

      <EditGoalModal
        open={editOpen}
        goal={goalForEdit}
        chains={chains}
        onClose={() => setEditOpen(false)}
        onSaved={() => {
          void refetchGoals();
          void sika.refetch();
        }}
      />
    </div>
  );
}

function HeaderBar({ tabsSlot }: { tabsSlot: React.ReactNode }): JSX.Element {
  return (
    <header
      style={{
        paddingBottom: 14,
        marginBottom: 18,
        borderBottom: "1px solid var(--sa-hairline)",
        display: "flex",
        alignItems: "center",
        gap: 14,
        flexWrap: "wrap",
      }}
    >
      <div
        style={{
          fontFamily: "var(--sa-font-mono)",
          fontSize: 11,
          color: "var(--sa-fg-muted)",
          letterSpacing: "0.18em",
          textTransform: "uppercase",
        }}
      >
        Workspace / SIKA · Goal Layer
      </div>
      {tabsSlot ? <div style={{ marginLeft: "auto" }}>{tabsSlot}</div> : null}
    </header>
  );
}

const panelShellStyle: React.CSSProperties = {
  marginBottom: 18,
  border: "1px solid var(--sa-hairline)",
  borderRadius: 4,
  background: "var(--sa-bg-elevated, transparent)",
};
